import { useState, useEffect, useRef, type RefObject } from "react";

interface PopoverPosition {
  top: number | null;
  left: number | null;
}

interface UsePopoverPositionProps {
  isOpen: boolean;
  anchorRef?: RefObject<HTMLElement>;
  mousePosition?: { x: number; y: number } | null;
  minTop?: number;
  maxOffsetFromBottom?: number;
  leftPadding?: number;
}

export function usePopoverPosition({
  isOpen,
  anchorRef,
  mousePosition,
  minTop = 48,
  maxOffsetFromBottom = 48,
  leftPadding = 16,
}: UsePopoverPositionProps) {
  const [position, setPosition] = useState<PopoverPosition>({
    top: null,
    left: null,
  });
  const modalRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const calculatePosition = () => {
      if (!modalRef.current) return;

      const portalRoot =
        document.getElementById("right-panel") ||
        document.getElementById("design-tokens-panel-root");

      if (!portalRoot) {
        setPosition({ top: null, left: null });
        return;
      }

      const panelRect = portalRoot.getBoundingClientRect();
      const modalHeight = modalRef.current.offsetHeight;
      const modalWidth = modalRef.current.offsetWidth;

      // Position modal to the left of the right panel (no overlap)
      const left = panelRect.left - modalWidth - leftPadding;

      // Position based on mouse position, anchor, or fixed location
      let top = mousePosition?.y
        ? mousePosition.y
        : anchorRef?.current
          ? anchorRef.current.getBoundingClientRect().top
          : panelRect.top + 100;

      const maxTop = panelRect.height - modalHeight - maxOffsetFromBottom;
      top = Math.max(top, minTop);
      top = Math.min(top, maxTop);

      setPosition({ top, left });
    };

    // Initial calculation
    calculatePosition();

    // Watch for size changes
    let resizeObserver: ResizeObserver | null = null;
    if (modalRef.current) {
      resizeObserver = new ResizeObserver(() => {
        requestAnimationFrame(calculatePosition);
      });
      resizeObserver.observe(modalRef.current);
    }

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [
    isOpen,
    anchorRef,
    mousePosition,
    minTop,
    maxOffsetFromBottom,
    leftPadding,
  ]);

  return {
    modalRef,
    computedTop: position.top,
    computedLeft: position.left,
    isPositioned: position.top !== null && position.left !== null,
  };
}
